import { useEffect, useRef, useState } from "react";
import { Loader2, User, Phone, Mail, ArrowRight, CheckCircle2, Download, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { getAttribution, getVisitorId, trackEvent } from "@/lib/analytics";
import { useLeadForm, leadField } from "@/lib/leadForms";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";


export default function SyllabusLeadModal({ open, onOpenChange, program, downloadUrl = "" }) {
  const { setUser } = useAuth();
  const cfg = useLeadForm("syllabus", open);
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [consent, setConsent] = useState(true);
  const timer = useRef(null);

  const fName = leadField(cfg, "name", { label: "Full Name", required: true });
  const fPhone = leadField(cfg, "phone", { label: "Phone Number", required: true });
  const fEmail = leadField(cfg, "email", { label: "Email Address", required: true });

  useEffect(() => () => clearInterval(timer.current), []);
  useEffect(() => {
    if (open) { setStep("form"); setError(""); setOtp(""); setFileUrl(downloadUrl || ""); }
  }, [open, downloadUrl]);

  const startCooldown = () => {
    setCooldown(60);
    clearInterval(timer.current);
    timer.current = setInterval(() => setCooldown((c) => (c <= 1 ? (clearInterval(timer.current), 0) : c - 1)), 1000);
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    const digits = form.phone.replace(/\D/g, "");
    if (fName.visible && fName.required && !form.name.trim()) { setError("Please enter your full name."); return; }
    if (fPhone.visible && fPhone.required && digits.length < 8) { setError("Please enter a valid phone number."); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { setError("Please enter a valid email address."); return; }
    if (!consent) { setError("Please tick the consent box to continue."); return; }
    setBusy(true);
    try {
      await api.post("/auth/otp/request", { email: form.email.trim() });
      setStep("otp");
      setOtp("");
      startCooldown();
      toast.success("Verification code sent to your email");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { email: form.email.trim(), otp, name: form.name.trim() });
      setUser(data);
      const res = await api.post("/leads/syllabus", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone,
        program_slug: program.slug,
        download_url: fileUrl,
        attribution: getAttribution(),
        visitor_id: getVisitorId(),
        consent_given: consent,
        consent_text: CONSENT_TEXT,
      });
      trackEvent("syllabus_lead", { program_slug: program.slug });
      const url = res.data.download_url || fileUrl;
      if (url) downloadFile(url, `${program.slug}-syllabus.pdf`);
      if (!url) {
        try { await api.post(`/programs/${program.slug}/brochure`, { name: form.name.trim(), email: form.email.trim() }); } catch (e) {}
      }
      setStep("done");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-xl py-6 pl-11 border-slate-200 focus-visible:ring-[#FFC72C]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden sm:max-w-3xl rounded-3xl border-0" data-testid="syllabus-modal">
        <div className="grid grid-cols-1 sm:grid-cols-[42%_58%]">
          <div className="hidden sm:block relative bg-[#FFF8E1]">
            <img src="/syllabus-creative.png" alt="Career growth with Training Basket" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="sm:hidden h-28 overflow-hidden relative bg-[#FFF8E1]">
            <img src="/syllabus-creative.png" alt="" className="w-full h-full object-cover object-top" />
          </div>

          <div className="p-6 sm:p-9">
            <span className="block w-8 h-1 rounded-full bg-[#FFC72C] mb-5" />

            {step === "form" && (
              <>
                <DialogTitle className="text-2xl sm:text-[1.7rem] font-extrabold tracking-tight text-[#17263B] leading-tight">{cfg.title || "Enter your details to download the program syllabus"}</DialogTitle>
                <DialogDescription className="text-sm text-slate-500 mt-2.5 leading-relaxed">{cfg.subtitle || `Fill in your details and we'll send you a secure OTP on your email address to access the ${program.title} syllabus.`}</DialogDescription>
                <form onSubmit={sendOtp} className="mt-6 flex flex-col gap-4">
                  {fName.visible && (
                    <div>
                      <Label htmlFor="sl-name" className="font-semibold text-slate-800">{fName.label} {fName.required && <span className="text-red-500">*</span>}</Label>
                      <div className="relative mt-1.5">
                        <User className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                        <Input id="sl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter your full name" className={inputCls} data-testid="syllabus-name-input" />
                      </div>
                    </div>
                  )}
                  {fPhone.visible && (
                    <div>
                      <Label htmlFor="sl-phone" className="font-semibold text-slate-800">{fPhone.label} {fPhone.required && <span className="text-red-500">*</span>}</Label>
                      <div className="mt-1.5">
                        <PhoneInput id="sl-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="Enter your phone number"
                          inputCls="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFC72C]" testid="syllabus-phone-input" />
                      </div>
                    </div>
                  )}
                  {fEmail.visible && (
                    <div>
                      <Label htmlFor="sl-email" className="font-semibold text-slate-800">{fEmail.label} <span className="text-red-500">*</span></Label>
                      <div className="relative mt-1.5">
                        <Mail className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                        <Input id="sl-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Enter your email address" className={inputCls} data-testid="syllabus-email-input" />
                      </div>
                      <p className="text-xs text-slate-400 mt-1.5">We will send a verification OTP to this email address.</p>
                    </div>
                  )}
                  {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5" data-testid="syllabus-error">{error}</p>}
                  <ConsentCheckbox checked={consent} onChange={setConsent} testid="syllabus-consent" />
                  <Button type="submit" disabled={busy} className="mt-1 rounded-xl py-6 font-bold bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] text-base" data-testid="syllabus-send-otp">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{cfg.button_label || "Send OTP to Email"} <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </form>
              </>
            )}

            {step === "otp" && (
              <>
                <DialogTitle className="text-2xl sm:text-[1.7rem] font-extrabold tracking-tight text-[#17263B] leading-tight">Verify your email</DialogTitle>
                <DialogDescription className="text-sm text-slate-500 mt-2.5 leading-relaxed">Enter the 6-digit code sent to <span className="font-semibold text-slate-700">{form.email}</span>. It expires in 10 minutes.</DialogDescription>
                <form onSubmit={verify} className="mt-6 flex flex-col gap-4">
                  <div>
                    <Label htmlFor="sl-otp" className="font-semibold text-slate-800">Verification code <span className="text-red-500">*</span></Label>
                    <Input id="sl-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="••••••"
                      className="mt-1.5 rounded-xl py-6 text-center text-xl tracking-[0.5em] font-mono border-slate-200 focus-visible:ring-[#FFC72C]" data-testid="syllabus-otp-input" />
                  </div>
                  {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5" data-testid="syllabus-error">{error}</p>}
                  <Button type="submit" disabled={busy || otp.length !== 6} className="rounded-xl py-6 font-bold bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] text-base" data-testid="syllabus-verify-button">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify & Download <ShieldCheck className="w-4 h-4 ml-2" /></>}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button type="button" className="text-slate-500 font-semibold" onClick={() => { setStep("form"); setOtp(""); setError(""); }} data-testid="syllabus-change-details">Change details</button>
                    <button type="button" disabled={cooldown > 0 || busy} onClick={sendOtp} className="text-[#9A7B0A] font-semibold disabled:text-slate-400" data-testid="syllabus-resend">
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                  </div>
                </form>
              </>
            )}

            {step === "done" && (
              <div className="flex flex-col items-center text-center py-6" data-testid="syllabus-success">
                <span className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-emerald-500" /></span>
                <DialogTitle className="text-2xl font-extrabold tracking-tight text-[#17263B] mt-5">{cfg.success_title || "You're verified!"}</DialogTitle>
                <DialogDescription className="text-sm text-slate-500 mt-2.5 leading-relaxed max-w-xs">{cfg.success_message || (fileUrl ? "Your syllabus download has started — we've also emailed you the link." : "We've emailed the brochure to your inbox.")} A counselor will reach out if you'd like a walkthrough.</DialogDescription>
                <div className="flex flex-col w-full gap-3 mt-7">
                  {fileUrl && (
                    <Button variant="outline" className="rounded-xl py-6 font-bold border-slate-300" onClick={() => downloadFile(fileUrl, `${program.slug}-syllabus.pdf`)} data-testid="syllabus-download-again">
                      <Download className="w-4 h-4 mr-2" /> Download again
                    </Button>
                  )}
                  <Button className="rounded-xl py-6 font-bold bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B]" onClick={() => onOpenChange(false)} data-testid="syllabus-done-button">Done</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
